// src/services/invoiceService.js - COMPLETO CON GASTOS Y GASTOS FIJOS
import api from './api';

const parseFileNameFromDisposition = (headerValue, fallbackName = 'file') => {
  const raw = String(headerValue || '');
  const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const simpleMatch = raw.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] || fallbackName;
};

const buildBlobFilePayload = (response, fallbackName = 'file') => ({
  blob: response.data,
  fileName: parseFileNameFromDisposition(response.headers?.['content-disposition'], fallbackName),
  contentType: response.headers?.['content-type'] || ''
});

const FALLBACK_STATUS_RULES = {
  invoice: {
    defaultStatus: 'pending',
    allowed: ['draft', 'pending', 'approved', 'paid', 'overdue', 'cancelled']
  },
  quote: {
    defaultStatus: 'draft',
    allowed: ['draft', 'sent', 'pending', 'approved', 'rejected', 'expired', 'cancelled']
  },
  purchase_order: {
    defaultStatus: 'pending',
    allowed: ['draft', 'pending', 'approved', 'in_progress', 'completed', 'paid', 'rejected', 'cancelled']
  }
};

const FALLBACK_STATUS_LABELS_BY_TYPE = {
  invoice: {
    draft: 'Draft',
    pending: 'Pending Payment',
    approved: 'Approved',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled'
  },
  quote: {
    draft: 'Draft',
    sent: 'Sent to Client',
    pending: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    expired: 'Expired',
    cancelled: 'Cancelled'
  },
  purchase_order: {
    draft: 'Draft',
    pending: 'Pending Approval',
    approved: 'Approved',
    in_progress: 'In Process',
    completed: 'Completed',
    paid: 'Paid',
    rejected: 'Rejected',
    cancelled: 'Cancelled'
  }
};

const formatStatusLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const buildStatusConfig = (rules) => {
  const safeRules = rules && typeof rules === 'object' ? rules : FALLBACK_STATUS_RULES;
  const types = ['invoice', 'quote', 'purchase_order'];

  const optionsByType = {};
  const defaultByType = {};
  const labelsByType = {};

  types.forEach((type) => {
    const rule = safeRules[type] || FALLBACK_STATUS_RULES[type];
    const allowed = Array.isArray(rule?.allowed) && rule.allowed.length
      ? rule.allowed
      : FALLBACK_STATUS_RULES[type].allowed;
    optionsByType[type] = allowed;
    defaultByType[type] = rule?.defaultStatus || FALLBACK_STATUS_RULES[type].defaultStatus;

    const fallbackLabels = FALLBACK_STATUS_LABELS_BY_TYPE[type] || {};
    const labels = {};
    allowed.forEach((status) => {
      labels[status] = fallbackLabels[status] || formatStatusLabel(status);
    });
    labelsByType[type] = labels;
  });

  return {
    rules: safeRules,
    optionsByType,
    defaultByType,
    labelsByType
  };
};

const FALLBACK_STATUS_CONFIG = buildStatusConfig(FALLBACK_STATUS_RULES);
let cachedStatusConfig = null;
let cachedNextDocumentNumbers = null;
const NEXT_DOCUMENT_NUMBERS_CACHE_TTL_MS = 30 * 1000;
const CATALOG_ITEMS_CACHE_TTL_MS = 60 * 1000;
const catalogItemsCache = new Map();

const getCacheEntry = (cacheStore, key) => {
  const entry = cacheStore.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    cacheStore.delete(key);
    return null;
  }
  return entry.value;
};

const setCacheEntry = (cacheStore, key, value, ttlMs) => {
  cacheStore.set(key, {
    value,
    expiresAt: Date.now() + ttlMs
  });
};

const clearDocumentSeedCache = () => {
  cachedNextDocumentNumbers = null;
};

const clearCatalogItemsCache = () => {
  catalogItemsCache.clear();
};

const invoiceService = {
  // ==========================================
  // 🧾 FACTURAS (INVOICES)
  // ==========================================
  getStatusRules: async (forceRefresh = false) => {
    if (!forceRefresh && cachedStatusConfig) return cachedStatusConfig;

    try {
      const response = await api.get('/invoices/status-rules');
      const config = buildStatusConfig(response?.data?.rules);
      cachedStatusConfig = config;
      return config;
    } catch (error) {
      if (cachedStatusConfig) return cachedStatusConfig;
      return FALLBACK_STATUS_CONFIG;
    }
  },

  getFallbackStatusRules: () => FALLBACK_STATUS_CONFIG,

  getNextDocumentNumbers: async (forceRefresh = false) => {
    if (!forceRefresh && cachedNextDocumentNumbers && cachedNextDocumentNumbers.expiresAt > Date.now()) {
      return cachedNextDocumentNumbers.value;
    }
    const response = await api.get('/invoices/next-numbers');
    cachedNextDocumentNumbers = {
      value: response.data,
      expiresAt: Date.now() + NEXT_DOCUMENT_NUMBERS_CACHE_TTL_MS
    };
    return response.data;
  },

  // Obtener todas las facturas (con filtros opcionales)
  getInvoices: async (type = null, status = null, options = {}) => {
    let url = '/invoices';
    const params = new URLSearchParams();
    
    if (type) params.append('type', type);
    if (status) params.append('status', status);
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.q) params.append('q', options.q);
    if (options?.project_id) params.append('project_id', String(options.project_id));
    
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await api.get(url);
    return response.data;
  },

  // Obtener factura por ID
  getInvoiceById: async (id) => {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },

  getQuoteForRenewal: async (id) => (await api.get(`/invoices/${id}`, { params: { read_only: true } })).data,
  renewQuote: async (id, payload) => {
    const response = await api.post(`/invoices/${id}/renew`, payload);
    clearDocumentSeedCache(); clearCatalogItemsCache();
    return response.data;
  },

  getDocumentVersions: async (id, before, signal) => (await api.get(`/invoices/${id}/versions`, { params: before ? { before } : {}, signal })).data,
  getDocumentVersion: async (id, versionId, signal) => (await api.get(`/invoices/${id}/versions/${versionId}`, { signal })).data,
  getDispatchFile: async (id, dispatchId, index) => (await api.get(`/invoices/${id}/dispatches/${dispatchId}/files/${index}`, { responseType: 'blob' })).data,
  getDocumentVersionFile: async (id, versionId, index) => (await api.get(`/invoices/${id}/versions/${versionId}/files/${index}`, { responseType: 'blob' })).data,

  // Obtener historial de cambios de status por documento
  getInvoiceStatusHistory: async (id, limit = 50) => {
    const response = await api.get(`/invoices/${id}/status-history?limit=${limit}`);
    return response.data;
  },

  getDocumentPayments: async (id) => {
    const response = await api.get(`/invoices/${id}/payments`);
    return response.data;
  },

  createDocumentPayment: async (id, payload) => {
    const response = await api.post(`/invoices/${id}/payments`, payload);
    return response.data;
  },

  deleteDocumentPayment: async (id, paymentId) => {
    const response = await api.delete(`/invoices/${id}/payments/${paymentId}`);
    return response.data;
  },

  // Descargar PDF oficial generado desde backend
  downloadInvoicePDF: async (id) => {
    const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  getInvoiceOfficialFile: async (id, { download = false } = {}) => {
    const suffix = download ? '/download' : '';
    const response = await api.get(`/invoices/${id}/file${suffix}`, { responseType: 'blob' });
    return buildBlobFilePayload(response, `document-${id}`);
  },

  getInvoiceDescriptionImage: async (id, index) => {
    const response = await api.get(`/invoices/${id}/description-images/${index}`, { responseType: 'blob' });
    return response.data;
  },

  // Crear factura / Cotización / PO
  duplicateInvoice: async (id) => {
    const response = await api.post(`/invoices/${id}/duplicate`);
    clearDocumentSeedCache();
    return response.data;
  },

  convertQuoteToInvoice: async (id) => {
    const response = await api.post(`/invoices/${id}/convert-to-invoice`);
    clearDocumentSeedCache();
    return response.data;
  },

  sendInvoiceByEmail: async (id, payload = {}) => {
    const formData = new FormData();
    formData.append('to', payload.to || '');
    formData.append('cc', payload.cc || '');
    formData.append('subject', payload.subject || '');
    formData.append('message', payload.message || '');
    if (payload.draft_id) formData.append('draft_id', String(payload.draft_id));
    if (payload.expected_revision) formData.append('expected_revision', payload.expected_revision);
    if (payload.client_request_id) formData.append('client_request_id', payload.client_request_id);
    if (Array.isArray(payload.attachments)) {
      payload.attachments.forEach((file) => formData.append('attachments', file));
    }

    const response = await api.post(`/invoices/${id}/send`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getLatestQuoteEmailDraft: async (id) => {
    const response = await api.get(`/invoices/${id}/email-drafts/latest`);
    return response.data;
  },

  getQuoteEmailDraft: async (id, draftId) => {
    const response = await api.get(`/invoices/${id}/email-drafts/${draftId}`);
    return response.data;
  },

  generateQuoteEmailDraft: async (id) => {
    const response = await api.post(`/invoices/${id}/email-drafts/generate`);
    return response.data;
  },

  updateQuoteEmailDraft: async (id, draftId, payload) => {
    const response = await api.patch(`/invoices/${id}/email-drafts/${draftId}`, payload);
    return response.data;
  },

  submitQuoteEmailDraft: async (id, draftId) => {
    const response = await api.post(`/invoices/${id}/email-drafts/${draftId}/submit`);
    return response.data;
  },

  approveQuoteEmailDraft: async (id, draftId, payload = {}) => {
    const response = await api.post(`/invoices/${id}/email-drafts/${draftId}/approve`, payload);
    return response.data;
  },

  rejectQuoteEmailDraft: async (id, draftId, reason) => {
    const response = await api.post(`/invoices/${id}/email-drafts/${draftId}/reject`, { reason });
    return response.data;
  },

  getEmailHistory: async (id, limit = 50) => {
    const response = await api.get(`/invoices/${id}/email-history`, { params: { limit } });
    return response.data;
  },

  getQuoteCommercialOverview: async (id) => {
    const response = await api.get(`/invoices/${id}/commercial-followup`);
    return response.data;
  },

  getFinanceBreakdown: async (params) => (await api.get('/invoices/stats/breakdown', { params })).data,

  exportFinanceCsv: async (filters = {}) => {
    const response = await api.get('/invoices/export/csv', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  exportFinanceExcel: async (filters = {}) => {
    const response = await api.get('/invoices/export/excel', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  createInvoice: async (invoiceData) => {
    const response = await api.post('/invoices', invoiceData);
    clearDocumentSeedCache();
    clearCatalogItemsCache();
    return response.data;
  },

  // Actualizar factura
  updateInvoice: async (id, invoiceData) => {
    const response = await api.put(`/invoices/${id}`, invoiceData);
    clearDocumentSeedCache();
    clearCatalogItemsCache();
    return response.data;
  },

  // Eliminar factura
  deleteInvoice: async (id) => {
    const response = await api.delete(`/invoices/${id}`);
    clearDocumentSeedCache();
    return response.data;
  },

  // Obtener estadísticas (KPIs)
  getInvoiceStats: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.dashboard) params.append('dashboard', 'true');
    const qs = params.toString();
    const response = await api.get(`/invoices/stats${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  // Obtener catálogo guardado de ítems cotizables
  getCatalogItems: async (search = '', limit = 150, forceRefresh = false) => {
    const cacheKey = JSON.stringify({
      q: String(search || '').trim().toLowerCase(),
      limit: Number(limit) || 0
    });
    if (!forceRefresh) {
      const cached = getCacheEntry(catalogItemsCache, cacheKey);
      if (cached) return cached;
    }

    const params = new URLSearchParams();
    if (search) params.append('q', search);
    if (limit) params.append('limit', String(limit));
    const qs = params.toString();
    const response = await api.get(`/invoices/catalog-items${qs ? `?${qs}` : ''}`);
    setCacheEntry(catalogItemsCache, cacheKey, response.data, CATALOG_ITEMS_CACHE_TTL_MS);
    return response.data;
  },

  // ==========================================
  // 💸 GASTOS (EXPENSES)
  // ==========================================

  // Obtener todos los gastos
  getExpenses: async (params = {}) => {
    const response = await api.get('/invoices/expenses', { params });
    return response.data;
  },

  getExpenseReceiptFile: async (expenseId, fileIndex = 0, { download = false } = {}) => {
    const suffix = download ? '/download' : '';
    const response = await api.get(`/invoices/expenses/${expenseId}/files/${fileIndex}${suffix}`, {
      responseType: 'blob'
    });
    return buildBlobFilePayload(response, `expense-${expenseId}-file-${fileIndex + 1}`);
  },

  // Crear gasto
  createExpense: async (expenseData) => {
    const response = await api.post('/invoices/expenses', expenseData);
    return response.data;
  },

  // ✅ ACTUALIZAR GASTO (Esta es la que faltaba)
  updateExpense: async (id, expenseData) => {
    const response = await api.put(`/invoices/expenses/${id}`, expenseData);
    return response.data;
  },

  // Eliminar gasto
  deleteExpense: async (id) => {
    const response = await api.delete(`/invoices/expenses/${id}`);
    return response.data;
  },

  // ==========================================
  // 🔁 GASTOS FIJOS (FIXED EXPENSES)
  // ==========================================

  // Obtener lista de plantillas de gastos fijos
  getFixedExpenses: async () => {
    const response = await api.get('/invoices/fixed-expenses');
    return response.data;
  },

  // Crear nueva plantilla de gasto fijo
  createFixedExpense: async (data) => {
    const response = await api.post('/invoices/fixed-expenses', data);
    return response.data;
  },

  // Eliminar plantilla de gasto fijo
  deleteFixedExpense: async (id) => {
    const response = await api.delete(`/invoices/fixed-expenses/${id}`);
    return response.data;
  },

  // Procesar pago (Convertir plantilla a gasto real del mes)
  processFixedExpense: async (id) => {
    const response = await api.post(`/invoices/fixed-expenses/${id}/process`);
    return response.data;
  }
};

export default invoiceService;
